/**
 * iPhones store photos as HEIC (HEVC-compressed HEIF). Neither the backend image
 * pipeline (sharp has no HEVC decoder) nor the AI providers can read it, so every
 * photo upload surface converts HEIC/HEIF to JPEG in the browser before the file
 * is validated and uploaded. The decoder (libheif as WebAssembly, ~3 MB) is only
 * loaded when a HEIC file is actually selected.
 */

const HEIC_MIME_TYPES = new Set(['image/heic', 'image/heif', 'image/heic-sequence', 'image/heif-sequence']);
const HEIC_EXTENSION = /\.(heic|heif)$/i;

/** JPEG quality of the converted photo (the backend downsizes for the AI anyway). */
const JPEG_QUALITY = 0.9;

/** True for a MIME type the browser reports for HEIC/HEIF photos. */
export function isHeicMimeType(mimeType: string): boolean {
  return HEIC_MIME_TYPES.has(mimeType.toLowerCase());
}

/**
 * True when the file is a HEIC/HEIF photo. Some browsers (e.g. Chrome on Windows)
 * report an empty type for .heic files, so the extension counts as well.
 */
export function isHeicFile(file: File): boolean {
  if (isHeicMimeType(file.type)) return true;
  return (file.type === '' || file.type === 'application/octet-stream') && HEIC_EXTENSION.test(file.name);
}

/** Converts one HEIC/HEIF photo to a JPEG file ("IMG_1234.HEIC" → "IMG_1234.jpg"). Rejects when it cannot be decoded. */
export async function convertHeicToJpeg(file: File): Promise<File> {
  const { heicTo } = await import('heic-to');
  const blob = await heicTo({ blob: file, type: 'image/jpeg', quality: JPEG_QUALITY });
  const baseName = file.name.replace(HEIC_EXTENSION, '') || 'photo';
  return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg', lastModified: file.lastModified });
}

/**
 * Converts every HEIC/HEIF file of a selection to JPEG and keeps the order. A file
 * that cannot be converted is returned unchanged, so the normal type validation
 * rejects it with its per-file message.
 */
export async function convertHeicFiles(files: readonly File[]): Promise<File[]> {
  return Promise.all(
    files.map(async (file) => {
      if (!isHeicFile(file)) return file;
      try {
        return await convertHeicToJpeg(file);
      } catch {
        return file;
      }
    }),
  );
}
